import { useCallback, useEffect, useState } from 'react';

import { Button, Dropdown, FieldLabel, Modal, showSnackbar, TextArea } from 'fluxo-ui';
import { CheckCircleIcon, TimesIcon } from 'fluxo-ui/icons';

import { Badge, Card, EmptyState, PageHeader } from '../components/common';
import questionFlagService, { FLAG_CATEGORY_LABELS, FlagStatus, QuestionFlag } from '../services/questionFlag.service';
import { formatDateTime } from '../utils/formatting';

import type { BadgeTone } from '../components/common/Badge';

const statusTones: Record<string, BadgeTone> = {
    pending: 'warning',
    under_review: 'accent',
    resolved: 'success',
    rejected: 'danger',
};

/** Moderator queue for issues reported by exam takers. */
const FlagsReviewPage: React.FC = () => {
    const [flags, setFlags] = useState<QuestionFlag[]>([]);
    const [statusFilter, setStatusFilter] = useState<FlagStatus | ''>('pending');
    const [loading, setLoading] = useState(true);
    const [resolveFlag, setResolveFlag] = useState<{ flag: QuestionFlag; status: FlagStatus } | null>(null);
    const [resolution, setResolution] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchFlags = useCallback(async () => {
        setLoading(true);
        try {
            const response = await questionFlagService.listFlags({
                status: statusFilter || undefined,
                limit: 100,
            });
            setFlags(response.flags || []);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to load flags', 'Error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchFlags();
    }, [fetchFlags]);

    const handleResolve = async () => {
        if (!resolveFlag) return;
        setSaving(true);
        try {
            await questionFlagService.updateFlag(resolveFlag.flag._id, resolveFlag.status, resolution.trim() || undefined);
            showSnackbar(resolveFlag.status === 'resolved' ? 'Flag resolved' : 'Flag rejected', 'Success', { type: 'success' });
            setResolveFlag(null);
            setResolution('');
            fetchFlags();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to update flag', 'Error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Reported Questions"
                description="Issues reported by exam takers. Resolve them after fixing the question, or reject invalid reports."
                className="mb-0"
                actions={
                    <div className="w-48">
                        <Dropdown
                            options={[
                                { label: 'Pending', value: 'pending' },
                                { label: 'Under Review', value: 'under_review' },
                                { label: 'Resolved', value: 'resolved' },
                                { label: 'Rejected', value: 'rejected' },
                                { label: 'All', value: '' },
                            ]}
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.value as FlagStatus | '')}
                        />
                    </div>
                }
            />

            <Card padding="none">
                {loading ? (
                    <div className="p-8 text-center text-slate-500 dark:text-slate-400">Loading reports...</div>
                ) : flags.length === 0 ? (
                    <EmptyState icon={CheckCircleIcon} title="Nothing to review" description="No reports match this filter." />
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {flags.map((flag) => {
                            const question = typeof flag.questionId === 'object' ? flag.questionId : null;
                            const reporter = typeof flag.flaggedBy === 'object' ? flag.flaggedBy : null;
                            return (
                                <div key={flag._id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <Badge tone={statusTones[flag.status] || 'neutral'} dot>
                                                    {flag.status.replace(/_/g, ' ')}
                                                </Badge>
                                                <Badge tone="neutral">{FLAG_CATEGORY_LABELS[flag.category] || flag.category}</Badge>
                                                <span className="text-xs text-slate-500 dark:text-slate-400">{formatDateTime(flag.createdAt)}</span>
                                            </div>
                                            <h3 className="mt-2 font-medium text-slate-900 dark:text-white truncate">
                                                {question?.title || 'Question'}
                                            </h3>
                                            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{flag.description}</p>
                                            {flag.resolution && (
                                                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2">
                                                    Resolution: {flag.resolution}
                                                </p>
                                            )}
                                            {reporter && (
                                                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                                                    Reported by {reporter.profile?.name || reporter.email || 'participant'}
                                                </p>
                                            )}
                                        </div>
                                        {(flag.status === 'pending' || flag.status === 'under_review') && (
                                            <div className="flex items-center gap-1 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-1.5 py-1">
                                                <Button
                                                    leftIcon={CheckCircleIcon}
                                                    size="sm"
                                                    variant="success"
                                                    layout="plain"
                                                    title="Mark resolved"
                                                    onClick={() => {
                                                        setResolution('');
                                                        setResolveFlag({ flag, status: 'resolved' });
                                                    }}
                                                />
                                                <span className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
                                                <Button
                                                    leftIcon={TimesIcon}
                                                    size="sm"
                                                    variant="danger"
                                                    layout="plain"
                                                    title="Reject report"
                                                    onClick={() => {
                                                        setResolution('');
                                                        setResolveFlag({ flag, status: 'rejected' });
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Resolve/Reject Modal */}
            <Modal
                isOpen={!!resolveFlag}
                onClose={() => !saving && setResolveFlag(null)}
                title={resolveFlag?.status === 'resolved' ? 'Resolve Report' : 'Reject Report'}
            >
                <div className="space-y-4 p-1">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        {resolveFlag?.status === 'resolved'
                            ? 'Confirm the issue has been fixed. The note below is stored with the report.'
                            : 'Reject this report if the question is correct as-is.'}
                    </p>
                    <FieldLabel label="Note" hint="Optional — visible to other reviewers">
                        <TextArea
                            value={resolution}
                            onChange={(e) => setResolution(e.value)}
                            placeholder={resolveFlag?.status === 'resolved' ? 'e.g. Fixed option B wording' : 'e.g. Question verified, options are correct'}
                            rows={3}
                        />
                    </FieldLabel>
                    <div className="flex gap-3 justify-end">
                        <Button label="Cancel" variant="default" layout="outlined" onClick={() => setResolveFlag(null)} disabled={saving} />
                        <Button
                            label={resolveFlag?.status === 'resolved' ? 'Resolve' : 'Reject'}
                            variant={resolveFlag?.status === 'resolved' ? 'success' : 'danger'}
                            onClick={handleResolve}
                            isLoading={saving}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default FlagsReviewPage;
